import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, Chip, LinearProgress } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import BlockIcon from "@mui/icons-material/Block";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { AuthContext } from "../../context/authContext";

const route = process.env.REACT_APP_URL_BACKEND;

// ─── Config por estado ──────────────────────────────────────────────────────
const STATUS_CONFIG = {
  verifying: {
    icon: HourglassEmptyIcon,
    color: "#d97706",
    bg: "#fffbeb",
    chipBg: "#fef3c7",
    chipColor: "#b45309",
    label: "Verificando...",
    headline: "Verificando tu pago",
    sub: "Estamos confirmando el estado de tu transacción. Un momento...",
    loading: true,
  },
  approved: {
    icon: CheckCircleOutlineIcon,
    color: "#16a34a",
    bg: "#f0fdf4",
    chipBg: "#dcfce7",
    chipColor: "#15803d",
    label: "Pago aprobado",
    headline: "¡Compra exitosa!",
    sub: "Tu pedido fue confirmado. Pronto recibirás un correo con los detalles.",
    loading: false,
  },
  in_process: {
    icon: HourglassEmptyIcon,
    color: "#d97706",
    bg: "#fffbeb",
    chipBg: "#fef3c7",
    chipColor: "#b45309",
    label: "En proceso",
    headline: "Tu pago está en proceso",
    sub: "MercadoPago está procesando tu pago. Puede demorar unos minutos.",
    loading: false,
  },
  rejected: {
    icon: BlockIcon,
    color: "#dc2626",
    bg: "#fef2f2",
    chipBg: "#fee2e2",
    chipColor: "#b91c1c",
    label: "Pago rechazado",
    headline: "El pago fue rechazado",
    sub: "Tu transacción no pudo completarse. Podés intentar con otro medio de pago.",
    loading: false,
  },
  error: {
    icon: WarningAmberIcon,
    color: "#dc2626",
    bg: "#fef2f2",
    chipBg: "#fee2e2",
    chipColor: "#b91c1c",
    label: "Error",
    headline: "Algo salió mal",
    sub: "No pudimos verificar tu pago. Si el problema persiste, contactá a soporte.",
    loading: false,
  },
  not_found: {
    icon: WarningAmberIcon,
    color: "#6b7280",
    bg: "#f9fafb",
    chipBg: "#f3f4f6",
    chipColor: "#374151",
    label: "Sin ID de pago",
    headline: "No se encontró el pago",
    sub: "No se recibió un ID de transacción válido. Verificá tu correo o contactá soporte.",
    loading: false,
  },
};

// ─── Componente principal ───────────────────────────────────────────────────
const BuyOK = () => {
  const [statusKey, setStatusKey] = useState("verifying");
  const [isConfirmed, setIsConfirmed] = useState(false);
  const { setIdCarrito } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const paymentId = searchParams.get("payment_id");

    if (!paymentId) {
      setStatusKey("not_found");
      return;
    }

    fetch(`${route}/mercadoPago/check-payment/${paymentId}`)
      .then((res) => res.json())
      .then((data) => {
        switch (data.status) {
          case "approved":
            setStatusKey("approved");
            setIsConfirmed(true);
            localStorage.removeItem("idCarrito");
            setIdCarrito(null);
            break;
          case "in_process":
            setStatusKey("in_process");
            break;
          case "rejected":
            setStatusKey("rejected");
            break;
          default:
            setStatusKey("in_process");
        }
      })
      .catch((err) => {
        console.error("Error verificando el pago:", err);
        setStatusKey("error");
      });
  }, []);

  const cfg = STATUS_CONFIG[statusKey];
  const Icon = cfg.icon;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f9fafb",
        p: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: { xs: "100%", sm: "440px" },
          bgcolor: "#fff",
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 20px 40px -8px rgba(0,0,0,0.12)",
        }}
      >
        {/* Barra de progreso mientras verifica */}
        {cfg.loading && (
          <LinearProgress
            sx={{ height: "3px", "& .MuiLinearProgress-bar": { bgcolor: "#d97706" } }}
          />
        )}

        {/* Banner de estado */}
        <Box
          sx={{
            bgcolor: cfg.bg,
            px: { xs: 3, sm: 4 },
            py: { xs: 4, sm: 5 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            borderBottom: `1px solid ${cfg.color}22`,
            transition: "background-color 0.4s ease",
          }}
        >
          <Box
            sx={{
              width: { xs: 68, sm: 80 },
              height: { xs: 68, sm: 80 },
              borderRadius: "50%",
              bgcolor: `${cfg.color}18`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2.5,
              animation: cfg.loading ? "pulse 1.4s ease-in-out infinite" : "none",
              "@keyframes pulse": {
                "0%, 100%": { transform: "scale(1)", opacity: 1 },
                "50%": { transform: "scale(1.07)", opacity: 0.75 },
              },
            }}
          >
            <Icon sx={{ fontSize: { xs: 36, sm: 42 }, color: cfg.color }} />
          </Box>

          <Chip
            label={cfg.label}
            size="small"
            sx={{
              mb: 1.5,
              bgcolor: cfg.chipBg,
              color: cfg.chipColor,
              fontWeight: 600,
              fontSize: "12px",
              letterSpacing: "0.04em",
              border: "none",
            }}
          />

          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ color: "#111827", mb: 1, fontSize: { xs: "20px", sm: "22px" } }}
          >
            {cfg.headline}
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: "#6b7280", lineHeight: 1.6, maxWidth: "320px" }}
          >
            {cfg.sub}
          </Typography>
        </Box>

        {/* Botones */}
        <Box
          sx={{
            px: { xs: 3, sm: 4 },
            py: { xs: 3, sm: 3.5 },
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Button
            fullWidth
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate("/")}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "15px",
              py: 1.3,
              bgcolor: "#111827",
              "&:hover": { bgcolor: "#374151" },
            }}
          >
            Seguir comprando
          </Button>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<ShoppingBagOutlinedIcon />}
            disabled={!isConfirmed}
            onClick={() => navigate("/profile/pedidos")}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "14px",
              py: 1.2,
              borderColor: "#e5e7eb",
              color: "#374151",
              "&:hover": { borderColor: "#9ca3af", bgcolor: "#f9fafb" },
              "&.Mui-disabled": { opacity: 0.45 },
            }}
          >
            Ver mis compras
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default BuyOK;